import { breakpoints } from '@galapagos/design-system/breakpoints';
import { colors } from '@galapagos/design-system/colors';
import { ensureFocusVisibleStyle } from '@galapagos/design-system/web';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing, SCREEN_GUTTER_WIDE_KEY } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';

type TypographyToken = keyof typeof typography;

export const SIGNUP_OFF_PATH_STYLE_ID = 'signup-off-path-style';
const SIGNUP_OFF_PATH_FOCUS_STYLE_ID = 'signup-off-path-focus-style';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

/**
 * The pieces of production's error and Book a call pages (Figma 1:494 and
 * 1:497) that no design-system component draws: the cream illustration panel
 * (production's cream is code's `coral.100`), body paragraphs, an inline link
 * drawn like the design-system Link, and the frame production embeds its
 * booking calendar in. On phones the frame cancels the column's screen-edge
 * gutter, as production's frame cancels Healthie's own inner padding, so it
 * runs to the screen edges; the cancel comes from the gutter token, so it can
 * never push past them. On desktop it keeps to the column. Every
 * value is a token. The link is a plain anchor, as in the signup footer, so
 * the analytics scanner reads its literal action id.
 */
export function signupOffPathCss(): string {
  return `
.signup-off-path__panel { display: flex; justify-content: center; padding: ${spacing.lg}px; background: ${colors.coral['100']}; border-radius: ${radius.xl}px; }
.signup-off-path__illustration { display: flex; justify-content: center; }
.signup-off-path__body { margin: 0; color: ${colors.text.primary}; ${font('b2Regular')} }
.signup-off-path__link { color: ${colors.text.primary}; text-decoration: underline; border-radius: ${radius.sm}px; ${font('b2Regular')} }
@media (hover: hover) {
  .signup-off-path__link:hover { text-decoration-thickness: ${sizing.stroke.strong}px; }
}
.signup-off-path__frame { margin: ${spacing.xl}px -${spacing[SCREEN_GUTTER_WIDE_KEY]}px 0; }
@media (min-width: ${breakpoints.column}px) {
  .signup-off-path__frame { margin-left: 0; margin-right: 0; }
}
.signup-off-path__frame-embed { display: block; width: 100%; border: 0; }
`;
}

/** Injects the off-path stylesheet once per document. No-op during prerender. */
export function ensureSignupOffPathStyle(): void {
  if (typeof document === 'undefined') return;
  ensureFocusVisibleStyle(SIGNUP_OFF_PATH_FOCUS_STYLE_ID, ['.signup-off-path__link']);
  if (document.getElementById(SIGNUP_OFF_PATH_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SIGNUP_OFF_PATH_STYLE_ID;
  style.textContent = signupOffPathCss();
  document.head.appendChild(style);
}
